import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from 'use-intl';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, ChevronRight } from 'lucide-react';
import { useCompanyOnboarding } from '@/hooks/useCompanyOnboarding';
import { CompanyContextStep } from '@/components/onboarding/CompanyContextStep';
import { LogoutButton } from '@/components/auth/LogoutButton';

type OnboardingStep = 'company' | 'company_context' | 'complete';

export default function OnboardingSteps() {
  const t = useTranslations('consultantOnboarding.steps');
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('company');

  const {
    isCompanyOnboardingComplete,
    isCompanyContextComplete,
    checkOnboardingStatus,
    isLoading
  } = useCompanyOnboarding();

  useEffect(() => {
    if (isCompanyOnboardingComplete && isCompanyContextComplete) {
      setCurrentStep('complete');
      setTimeout(() => navigate('/overview'), 1000);
    } else if (isCompanyOnboardingComplete && !isCompanyContextComplete) {
      setCurrentStep('company_context');
    } else {
      setCurrentStep('company');
    }
  }, [isCompanyOnboardingComplete, isCompanyContextComplete, navigate]);

  const handleCompanyContextComplete = () => {
    checkOnboardingStatus();
  };

  const handleFinishOnboarding = () => {
    navigate('/overview');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LogoutButton />
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <LogoutButton />
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('title')}</h1>
          <p className="text-gray-600 mt-2">
            {t('subtitle')}
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center mb-8 space-x-4">
          <div className={`flex items-center space-x-2 ${isCompanyOnboardingComplete ? 'text-green-600' : currentStep === 'company' ? 'text-blue-600' : 'text-gray-400'}`}>
            {isCompanyOnboardingComplete ? (
              <CheckCircle className="h-5 w-5" />
            ) : (
              <div className="h-5 w-5 rounded-full border-2 border-current"></div>
            )}
            <span className="text-sm font-medium">{t('companySetup')}</span>
          </div>

          <ChevronRight className="h-4 w-4 text-gray-400" />

          <div className={`flex items-center space-x-2 ${isCompanyContextComplete ? 'text-green-600' : currentStep === 'company_context' ? 'text-blue-600' : 'text-gray-400'}`}>
            {isCompanyContextComplete ? (
              <CheckCircle className="h-5 w-5" />
            ) : (
              <div className="h-5 w-5 rounded-full border-2 border-current"></div>
            )}
            <span className="text-sm font-medium">{t('companyInfo')}</span>
          </div>

          <ChevronRight className="h-4 w-4 text-gray-400" />

          <div className={`flex items-center space-x-2 ${currentStep === 'complete' ? 'text-green-600' : 'text-gray-400'}`}>
            {currentStep === 'complete' ? (
              <CheckCircle className="h-5 w-5" />
            ) : (
              <div className="h-5 w-5 rounded-full border-2 border-current"></div>
            )}
            <span className="text-sm font-medium">{t('complete')}</span>
          </div>
        </div>

        {/* Current Step Content */}
        <div className="space-y-6">
          {currentStep === 'company' && (
            <Card>
              <CardHeader>
                <CardTitle>{t('companyRequired.title')}</CardTitle>
                <CardDescription>
                  {t('companyRequired.body')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={() => navigate('/join-company')} className="w-full">
                  {t('companyRequired.action')}
                </Button>
              </CardContent>
            </Card>
          )}

          {currentStep === 'company_context' && (
            <CompanyContextStep
              isComplete={isCompanyContextComplete}
              onComplete={handleCompanyContextComplete}
            />
          )}

          {currentStep === 'complete' && (
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-6 w-6 text-green-500" />
                  <CardTitle className="text-green-700">{t('done.title')}</CardTitle>
                </div>
                <CardDescription>
                  {t('done.body')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-medium text-green-900 mb-2">{t('done.whatsNext')}</h3>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>{t('done.connectBank')}</li>
                    <li>{t('done.upload')}</li>
                    <li>{t('done.payments')}</li>
                  </ul>
                </div>

                <Button onClick={handleFinishOnboarding} className="w-full">
                  {t('done.dashboard')}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}